import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { RegraTributariaIndicadoresUi } from '@shared/constants/motor-fiscal/regra-tributaria-indicadores.util';
import { RegraTributariaBaseCalculoSectionComponent } from './sections/base-calculo-section/regra-tributaria-base-calculo-section.component';
import { RegraTributariaCbsSectionComponent } from './sections/cbs-section/regra-tributaria-cbs-section.component';
import { RegraTributariaIbsUfSectionComponent } from './sections/ibs-uf-section/regra-tributaria-ibs-uf-section.component';
import { RegraTributariaIbsMunicipioSectionComponent } from './sections/ibs-municipio-section/regra-tributaria-ibs-municipio-section.component';

const minimoZero = Validators.min(0);

/** Aba IBS/CBS: ativação geral do tributo, base de cálculo compartilhada e as seções CBS, IBS UF e IBS Município. */
@Component({
    selector: 'app-regra-tributaria-ibs-cbs-tab',
    standalone: true,
    imports: [
        CommonModule,
        AtivacaoSectionCardComponent,
        AlertCardComponent,
        RegraTributariaBaseCalculoSectionComponent,
        RegraTributariaCbsSectionComponent,
        RegraTributariaIbsUfSectionComponent,
        RegraTributariaIbsMunicipioSectionComponent,
    ],
    templateUrl: './regra-tributaria-ibs-cbs-tab.component.html',
    styleUrl: './regra-tributaria-ibs-cbs-tab.component.css',
})
export class RegraTributariaIbsCbsTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() isModoPadrao = false;
    @Input() indicadoresUi: RegraTributariaIndicadoresUi | null = null;
    @Input() formulasIbsCbs: FormulaCatalogoItemStruct[] = [];
    @Input() formulasCbs: FormulaCatalogoItemStruct[] = [];
    @Input() formulasIbsUf: FormulaCatalogoItemStruct[] = [];
    @Input() formulasIbsMunicipio: FormulaCatalogoItemStruct[] = [];

    public readonly cashbackTooltip = 'Devolução de tributo (cashback) disponível apenas para regras aplicadas ao modelo 55 (NF-e). Selecione o modelo 55 em Modelos de Documento para habilitar.';

    ngOnInit(): void {
        // Ao ativar o tributo, as alíquotas passam a ser obrigatórias; redução, diferimento e
        // cashback tornam obrigatórios seus campos apenas quando também estiverem ativos.
        [this.cbsForm, this.ibsUfForm, this.ibsMunicipioForm].forEach(g => {
            g.get('reducaoAtivo')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
            g.get('diferimentoAtivo')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
            g.get('devolucaoAtivo')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        });
        this.ibsForm.get('reducaoBaseCalculoAtivo')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.ativacaoControl.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.form.get('modelosDocumento')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.sincronizarObrigatoriedade();
    }

    private sincronizarObrigatoriedade(): void {
        const ativo = !!this.ativacaoControl.value;
        const cashbackHabilitado = this.cashbackHabilitado;
        // Redutor de base: ligado exige o percentual (0 a 100); desligado fica nulo e a base sai cheia.
        const reducaoBaseCalculo = ativo && !!this.ibsForm.get('reducaoBaseCalculoAtivo')?.value;
        const percentualReducaoBaseCalculo = this.ibsForm.get('percentualReducaoBaseCalculo');
        if (!reducaoBaseCalculo && percentualReducaoBaseCalculo?.value != null) {
            percentualReducaoBaseCalculo.setValue(null, { emitEvent: false });
        }
        this.aplicarObrigatorio(percentualReducaoBaseCalculo, reducaoBaseCalculo);
        this.sincronizarSubGrupo(this.cbsForm, ativo, cashbackHabilitado);
        this.sincronizarSubGrupo(this.ibsUfForm, ativo, cashbackHabilitado);
        this.sincronizarSubGrupo(this.ibsMunicipioForm, ativo, cashbackHabilitado);
    }

    private sincronizarSubGrupo(grupo: FormGroup, tributoAtivo: boolean, cashbackHabilitado: boolean): void {
        const reducao = tributoAtivo && !!grupo.get('reducaoAtivo')?.value;
        const diferimento = tributoAtivo && !!grupo.get('diferimentoAtivo')?.value;
        this.aplicarObrigatorio(grupo.get('pRedAliq'), reducao);
        this.aplicarObrigatorio(grupo.get('pDif'), diferimento);

        const devolucaoControl = grupo.get('devolucaoAtivo');
        const cashbackPermitido = tributoAtivo && cashbackHabilitado;
        if (devolucaoControl) {
            if (cashbackPermitido && devolucaoControl.disabled) {
                devolucaoControl.enable({ emitEvent: false });
            } else if (!cashbackPermitido) {
                // Sem cashback a devolução não fica ligada, nem pré-ligada pelo indicador do CST.
                if (devolucaoControl.enabled) devolucaoControl.disable({ emitEvent: false });
                if (devolucaoControl.value) devolucaoControl.setValue(false, { emitEvent: false });
            }
        }
        const devolucao = cashbackPermitido && !!devolucaoControl?.value;
        this.aplicarObrigatorio(grupo.get('pDevTrib'), devolucao);
    }

    private aplicarObrigatorio(control: AbstractControl | null, obrigatorio: boolean): void {
        if (!control) return;
        // Preenchido é não vazio: zero é aceito, negativo não (o teto de 100 vem da máscara de percentual).
        if (obrigatorio) {
            control.addValidators([Validators.required, minimoZero]);
        } else {
            control.removeValidators([Validators.required, minimoZero]);
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    public get ibsForm(): FormGroup {
        return this.form.get('ibsCbs') as FormGroup;
    }

    public get ativacaoControl(): FormControl {
        return this.ibsForm.get('isAtivo') as FormControl;
    }

    public get cbsForm(): FormGroup {
        return this.ibsForm.get('cbs') as FormGroup;
    }

    public get ibsUfForm(): FormGroup {
        return this.ibsForm.get('ibsUf') as FormGroup;
    }

    public get ibsMunicipioForm(): FormGroup {
        return this.ibsForm.get('ibsMunicipio') as FormGroup;
    }

    /** Cashback habilitado apenas para o modelo 55; no modo padrão a regra vale para todos os modelos. */
    public get cashbackHabilitado(): boolean {
        if (this.isModoPadrao) return true;
        const modelos: string[] = this.form.get('modelosDocumento')?.value ?? [];
        return modelos.includes('55');
    }
}
